package com.htmlagent.site.api

import com.htmlagent.shared.ApiProblem

/**
 * Why a call to the API did not produce what was asked for.
 *
 * It is a closed hierarchy so the screen decides what to say for each case with an exhaustive
 * `when`: adding a reason stops compiling instead of falling through to a generic message.
 * Nothing here carries text for the person — that belongs to the UI layer, which is what makes
 * the same failure sayable in another language later.
 */
sealed interface ApiFailure {

    /**
     * The backend answered with the error contract of `:shared`: a stable [ApiProblem.code] and
     * a [ApiProblem.detail] already written to be read.
     */
    data class Problem(val status: Int, val problem: ApiProblem) : ApiFailure

    /**
     * A non-OK status whose body is not an [ApiProblem]: a proxy, a gateway, a 502 with HTML.
     * The status is kept because it is the only thing left to diagnose with.
     */
    data class Unexpected(val status: Int) : ApiFailure

    /** The request never got an answer: the server is down, there is no network, CORS blocked it. */
    data class Unreachable(val cause: Throwable) : ApiFailure

    /** There was an answer and it was OK, but its body is not what the contract says it is. */
    data class Malformed(val cause: Throwable) : ApiFailure
}
